import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { examApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { 
  BookOpen, 
  Clock, 
  Calendar, 
  Lock, 
  Play, 
  Filter,
  ChevronDown,
  Search,
  Zap
} from 'lucide-react';
import { 
  AnimatedCard, 
  StaggerContainer, 
  StaggerItem, 
  FadeIn,
  LoadingSpinner,
  SkeletonCard 
} from '../components/ui/AnimatedCard';

export function Exams() {
  const { user } = useAuthStore();
  const [activeStatus, setActiveStatus] = useState<string>('all');
  const [selectedClass, setSelectedClass] = useState<number | undefined>(user?.classId);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch ALL exams (no status filter) so we can count and filter client-side
  const { data: allExams, isLoading } = useQuery({
    queryKey: ['exams-all'],
    queryFn: async () => {
      const res = await examApi.getExams({});
      return res.data;
    },
  });

  // Get unique classes from exams
  const classes = useMemo(() => {
    if (!allExams) return [];
    const classMap = new Map();
    allExams.forEach((exam: any) => {
      if (exam.subject?.classId && !classMap.has(exam.subject.classId)) {
        classMap.set(exam.subject.classId, {
          id: exam.subject.classId,
          name: `Class ${exam.subject.classId}`,
        });
      }
    });
    return Array.from(classMap.values());
  }, [allExams]);

  // Filter exams based on active status, class, and search
  const filteredExams = useMemo(() => {
    if (!allExams) return [];
    
    return allExams.filter((exam: any) => {
      // Status filter
      if (activeStatus !== 'all' && exam.status !== activeStatus) return false;
      
      // Class filter
      if (selectedClass && exam.subject?.classId !== selectedClass) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const titleMatch = (exam.titleBn || exam.title || '').toLowerCase().includes(query);
        const subjectMatch = (exam.subject?.nameBn || exam.subject?.name || '').toLowerCase().includes(query);
        if (!titleMatch && !subjectMatch) return false;
      }
      
      return true;
    });
  }, [allExams, activeStatus, selectedClass, searchQuery]);

  // Count exams by status
  const statusCounts = useMemo(() => {
    if (!allExams) return {};
    const counts: Record<string, number> = { all: allExams.length };
    allExams.forEach((exam: any) => {
      counts[exam.status] = (counts[exam.status] || 0) + 1;
    });
    return counts;
  }, [allExams]);

  const statusOptions = [
    { value: 'all', label: 'All Exams', color: 'bg-primary-600 text-white' },
    { value: 'published', label: 'Published', color: 'bg-secondary-100 text-secondary-700' },
    { value: 'running', label: 'Running', color: 'bg-green-100 text-green-700' },
    { value: 'upcoming', label: 'Upcoming', color: 'bg-amber-100 text-amber-700' },
    { value: 'completed', label: 'Completed', color: 'bg-slate-100 text-slate-700' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Available Exams</h1>
            <p className="text-slate-500 mt-1">Choose your class and start practicing</p>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </FadeIn>

      {/* Search */}
      <FadeIn delay={0.1}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search exams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
          />
        </div>
      </FadeIn>

      {/* Filters */}
      <motion.div
        initial={false}
        animate={{ height: showFilters ? 'auto' : 0, opacity: showFilters ? 1 : 0 }}
        className="overflow-hidden"
      >
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
          {/* Class Filter */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Class</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedClass(undefined)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedClass
                    ? 'bg-primary-100 text-primary-700 ring-2 ring-offset-2 ring-primary-500'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All Classes
              </button>
              {classes.map((cls: any) => (
                <button
                  key={cls.id}
                  onClick={() => setSelectedClass(cls.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedClass === cls.id
                      ? 'bg-primary-100 text-primary-700 ring-2 ring-offset-2 ring-primary-500'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {cls.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Status Tabs - with REAL counts */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {statusOptions.map((option) => {
          const count = statusCounts[option.value] || 0;
          const isActive = activeStatus === option.value;
          
          return (
            <button
              key={option.value}
              onClick={() => setActiveStatus(option.value)}
              className={`px-6 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {option.label}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                isActive ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Exams Grid */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : filteredExams?.length === 0 ? (
        <FadeIn>
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-100 rounded-2xl mb-4">
              <BookOpen className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No exams found</h3>
            <p className="text-slate-500">Try changing your filters or check back later</p>
          </div>
        </FadeIn>
      ) : (
        <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredExams?.map((exam: any) => (
            <StaggerItem key={exam.id}>
              <AnimatedCard className="h-full flex flex-col">
                {/* Card Header */}
                <div className="p-6 flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      exam.type === 'live' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {exam.type === 'live' ? '🔴 Live' : '💚 Practice'}
                    </span>
                    {exam.isPremium && (
                      <span className="flex items-center gap-1 px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold">
                        <Zap className="w-3 h-3" />
                        Premium
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                    {exam.titleBn || exam.title}
                  </h3>

                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                    <BookOpen className="w-4 h-4" />
                    <span>{exam.subject?.nameBn || exam.subject?.name}</span>
                    {exam.chapter && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span>{exam.chapter.nameBn || exam.chapter.name}</span>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>{exam.durationMinutes} mins</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Zap className="w-4 h-4 text-slate-400" />
                      <span>{exam.totalMarks} marks</span>
                    </div>
                    {exam.startTime && (
                      <div className="flex items-center gap-2 text-sm text-slate-600 col-span-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{new Date(exam.startTime).toLocaleString('bn-BD')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-6 pb-6">
                  <Link
                    to={`/exams/${exam.id}`}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    Start Exam
                  </Link>
                </div>
              </AnimatedCard>
            </StaggerItem>
          ))}
        </StaggerContainer>
      )}
    </div>
  );
}
